(function () {
  if (customElements.get('wishlist-heart')) return;

  /* =====================================================================
     SWISH (Wishlist King) STOREFRONT BRIDGE
     ---------------------------------------------------------------------
     Swish is the Appmate "Wishlist King" app. Its storefront runtime is the
     @appmate/wishlist SDK, loaded via the "App Settings" app embed
     (Theme Customizer -> App embeds). That embed MUST stay enabled — it boots
     the SDK and assigns the live app instance to `window.WishlistKing`
     (a @appmate/wishlist `WishlistApp`). The embed is independent of Swish's
     own button app blocks, which we removed in favour of this custom heart.

     The integration surface (confirmed against @appmate/wishlist@5.29.0 and
     Swish's own product-button component, which does exactly this):
       window.WishlistKing.addWishlistItem({ productHandle, productId, variantId })
       window.WishlistKing.removeWishlistItem({ wishlistItemId })   // needs the item id
       window.WishlistKing.events.subscribe('<event>', handler)     // app:ready, wishlist:*:success, wishlist:load
       window.WishlistKing.state.wishlist.items[]                   // { id, product:{id,handle}, variantId }
     addWishlistItem HARD-REQUIRES productHandle (throws without it) — the snippet
     passes data-product-handle for this reason.

     Everything below is wrapped so the heart is NEVER inert: if WishlistKing
     never boots, or any call throws/rejects, we silently fall back to the
     localStorage interim store. State writes go to ONE backend at a time.
     ===================================================================== */

  // The live Appmate WishlistApp instance, once booted. Resolved via app:ready.
  function getSwish() {
    const wk = window.WishlistKing;
    // Only treat it as ready once its public methods exist.
    return wk && typeof wk.addWishlistItem === 'function' ? wk : null;
  }

  // Resolve once WishlistKing's runtime is on the page. It boots async via the
  // app embed and announces itself with the `app:ready` event on its own bus;
  // we also poll as a belt-and-braces fallback. Resolves null on timeout so the
  // heart degrades to localStorage. Never throws.
  let _swishReadyPromise = null;
  function whenSwishReady(timeout = 8000) {
    if (_swishReadyPromise) return _swishReadyPromise;
    _swishReadyPromise = new Promise((resolve) => {
      let settled = false;
      let poll;
      const done = (val) => {
        if (settled) return;
        settled = true;
        if (poll) clearInterval(poll);
        resolve(val);
      };

      const existing = getSwish();
      if (existing) return done(existing);

      // Subscribe to the SDK's own ready signal if the bus is already present.
      try {
        const wk = window.WishlistKing;
        if (wk && wk.events && typeof wk.events.subscribe === 'function') {
          wk.events.subscribe('app:ready', () => done(getSwish()));
        }
      } catch (e) {
        /* bus not ready yet — the poll below covers it */
      }

      const start = Date.now();
      poll = setInterval(() => {
        const s = getSwish();
        if (s) return done(s);
        if (Date.now() - start > timeout) done(null);
      }, 150);
    });
    return _swishReadyPromise;
  }

  // Find the live wishlist item matching a product on the WishlistKing instance.
  // Reads state.wishlist.items defensively (minified SDK internals may shift);
  // any failure returns null so callers fall back cleanly. Matches on numeric
  // product id (item.product.id may be a Shopify GID) or handle.
  function findSwishItem(wk, productId, productHandle) {
    try {
      const items =
        wk && wk.state && wk.state.wishlist && wk.state.wishlist.items;
      if (!Array.isArray(items)) return null;
      const pid = numId(productId);
      return (
        items.find((it) => {
          const ip = it && it.product;
          if (!ip) return false;
          const byId = pid != null && numId(ip.id) === pid;
          const byHandle = productHandle && ip.handle === productHandle;
          return byId || byHandle;
        }) || null
      );
    } catch (e) {
      return null;
    }
  }

  // ---- localStorage fallback (only used when Swish is absent) ----
  const STORAGE_KEY = 'velo:wishlist:interim';

  function readStore() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(map) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (e) {
      /* storage full / blocked — fail silently, state stays in-memory */
    }
  }

  class WishlistHeart extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.wishlist-heart__btn');
      if (!this.btn) return;

      this.productId = this.dataset.productId;
      this.productHandle = this.dataset.productHandle;
      this.variantId = this.dataset.variantId;

      this._onClick = this.onClick.bind(this);
      this.btn.addEventListener('click', this._onClick);

      // Sync the heart to the current variant when the PDP variant changes.
      // VELO/Symmetry dispatches `on:variant:change` on the `.js-product` container
      // (same event store-availability.js listens to).
      this.productEl = this.closest('.js-product');
      if (this.productEl) {
        this._onVariant = this.onVariantChange.bind(this);
        this.productEl.addEventListener('on:variant:change', this._onVariant);
      }

      // Reflect cached/interim state immediately, then reconcile against the
      // Swish list once its runtime is ready (and on any external change).
      this.saved = this.isInWishlistCached();
      this.render();

      whenSwishReady().then((swish) => {
        this.swish = swish || null;
        // Re-render whenever the wishlist changes elsewhere (drawer, wishlist
        // page, add/remove via another heart) so this one stays in sync. The
        // WishlistKing event bus fires these on its own dispatcher, not window.
        if (this.swish && this.swish.events && typeof this.swish.events.subscribe === 'function') {
          this._swishSubs = [];
          this._onSwishChange = () => this.refreshSavedState();
          [
            'wishlist:add:success',
            'wishlist:remove:success',
            'wishlist:change-variant:success',
            'wishlist:clear:success',
            'wishlist:load',
          ].forEach((name) => {
            try {
              // subscribe() returns a disposable on Appmate's bus; store it if so.
              const sub = this.swish.events.subscribe(name, this._onSwishChange);
              if (sub) this._swishSubs.push(sub);
            } catch (e) {
              /* ignore a single bad subscription — others still bind */
            }
          });
        }
        this.refreshSavedState();
      });
    }

    disconnectedCallback() {
      if (this.btn) this.btn.removeEventListener('click', this._onClick);
      if (this.productEl && this._onVariant) {
        this.productEl.removeEventListener('on:variant:change', this._onVariant);
      }
      if (this._swishSubs) {
        this._swishSubs.forEach((sub) => {
          try {
            if (typeof sub === 'function') sub();
            else if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
            else if (sub && typeof sub.dispose === 'function') sub.dispose();
          } catch (e) {
            /* best-effort teardown */
          }
        });
        this._swishSubs = null;
      }
    }

    onVariantChange(evt) {
      const variant = evt && evt.detail && evt.detail.variant;
      if (variant && variant.id != null) {
        this.variantId = String(variant.id);
        this.dataset.variantId = this.variantId;
      }
      // Re-check saved state for the new variant against Swish.
      this.refreshSavedState();
    }

    onClick() {
      // Optimistically flip the UI, then perform the (possibly async) write.
      // If the write rejects, refreshSavedState() reconciles back to truth.
      const wasSaved = this.saved;
      const detail = { productId: this.productId, variantId: this.variantId };

      this.saved = !wasSaved;
      this.render();

      const op = wasSaved ? this.removeFromWishlist() : this.addToWishlist();
      Promise.resolve(op)
        .then(() => {
          this.dispatchEvent(
            new CustomEvent(wasSaved ? 'wishlist:remove' : 'wishlist:add', {
              bubbles: true,
              detail,
            })
          );
        })
        .catch(() => {
          // Write failed — pull the real state back from Swish and re-render.
          this.refreshSavedState();
        });
    }

    render() {
      const saved = !!this.saved;
      this.btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.btn.setAttribute(
        'aria-label',
        saved ? 'Remove from wishlist' : 'Add to wishlist'
      );
    }

    // Recompute saved state for the current product/variant from the live
    // backend (Swish if present, else the interim store) and re-render.
    refreshSavedState() {
      Promise.resolve(this.isInWishlist())
        .then((saved) => {
          this.saved = !!saved;
          this.render();
        })
        .catch(() => {
          /* leave last-known state on transient API errors */
        });
    }

    // The key we track on. Product-level is the right granularity for a wishlist
    // (the variant rides along in the event detail for whoever consumes it).
    key() {
      return String(this.productId);
    }

    /* =====================================================================
       SWISH WISHLIST KING — INTEGRATION SURFACE
       ---------------------------------------------------------------------
       These three methods talk to the live WishlistKing (Appmate WishlistApp)
       instance when present, and fall back to the interim localStorage store
       when it is not — so the heart is never inert and there are no
       double-writes. add/remove return Promises; isInWishlist() returns a
       Promise<boolean>. Every Swish call is guarded; on any throw/reject the
       caller's catch reconciles (and writes silently degrade to localStorage).
         WishlistKing.addWishlistItem({ productHandle, productId, variantId })
         WishlistKing.removeWishlistItem({ wishlistItemId })   // id from state
         WishlistKing.state.wishlist.items[]                   // membership read
       ===================================================================== */

    addToWishlist() {
      const wk = this.swish;
      if (wk && typeof wk.addWishlistItem === 'function' && this.productHandle) {
        try {
          // addWishlistItem hard-requires productHandle; numeric ids are optional
          // but help the SDK resolve the right product/variant.
          return Promise.resolve(
            wk.addWishlistItem({
              productHandle: this.productHandle,
              productId: numId(this.productId),
              variantId: numId(this.variantId),
            })
          );
        } catch (e) {
          /* fall through to localStorage */
        }
      }
      // Fallback: interim store.
      const map = readStore();
      map[this.key()] = { variantId: this.variantId, ts: Date.now() };
      writeStore(map);
      return Promise.resolve();
    }

    removeFromWishlist() {
      const wk = this.swish;
      if (wk && typeof wk.removeWishlistItem === 'function') {
        try {
          const item = findSwishItem(wk, this.productId, this.productHandle);
          if (item && item.id != null) {
            return Promise.resolve(
              wk.removeWishlistItem({ wishlistItemId: item.id })
            );
          }
        } catch (e) {
          /* fall through to localStorage */
        }
      }
      // Fallback: interim store.
      const map = readStore();
      delete map[this.key()];
      writeStore(map);
      return Promise.resolve();
    }

    isInWishlist() {
      const wk = this.swish;
      if (wk && wk.state) {
        // Read membership straight off the live wishlist state (guarded).
        const item = findSwishItem(wk, this.productId, this.productHandle);
        if (item) return Promise.resolve(true);
        // No match in a populated/empty list -> not saved. If state is missing
        // (mid-load), findSwishItem returns null and we fall back below.
        try {
          const items = wk.state.wishlist && wk.state.wishlist.items;
          if (Array.isArray(items)) return Promise.resolve(false);
        } catch (e) {
          /* state not ready — fall back */
        }
      }
      // Fallback: interim store.
      return Promise.resolve(this.isInWishlistCached());
    }

    // Synchronous best-guess for instant first paint (interim store only).
    // The async isInWishlist() reconciles against Swish once it is ready.
    isInWishlistCached() {
      return Object.prototype.hasOwnProperty.call(readStore(), this.key());
    }
    /* ===================== END SWISH WIRING SURFACE ===================== */
  }

  // Coerce a Shopify gid/string id to the numeric id Swish expects, else null.
  function numId(v) {
    if (v == null) return null;
    const m = String(v).match(/\d+/g);
    return m ? Number(m[m.length - 1]) : null;
  }

  customElements.define('wishlist-heart', WishlistHeart);
})();
